import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import { ReportsUrl } from "@saleor/reports/overview/urls";
import { InvalidThreeMonth } from "@saleor/reports/utils/misc";
import { reportsDownload } from "@saleor/reports/utils/utils";
import useMallSearch from "@saleor/searches/useMallSearch";
import { sellerDetailsUrl } from "@saleor/seller/urls";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SellerOrdersListPage from "../../components/SellerOrdersListPage";
import {
  useInitialSellerOrderFilterDataQuery,
  useSellerOrdersDownload,
  useSellerOrdersQuery
} from "../../queries";
import {
  SellerOrdersListUrl,
  SellerOrdersListUrlDialog,
  SellerOrdersListUrlQueryParams
} from "../../url";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface SellerOrdersListProps {
  params: SellerOrdersListUrlQueryParams;
}

export const SellerOrdersList: React.FC<SellerOrdersListProps> = ({
  params
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();

  const intl = useIntl();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");

  const sheetName = "Seller Orders";
  const fileName = `${reportsDownload.sellerOrders.filename}_${dateNow}.xlsx`;
  const currencySymbol = shop?.defaultCurrency || "PHP";

  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const { data: initialFilterData } = useInitialSellerOrderFilterDataQuery({
    variables: {
      mall: params.mall
    }
  });

  const filterOpts = getFilterOpts(params, {
    initial: maybe(
      () => initialFilterData.malls.edges.map(edge => edge.node) || []
    ),
    search: searchMall
  });

  const reportVariables = React.useMemo(
    () => ({
      ...paginationState,
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1000
    }),
    [params]
  );

  React.useEffect(
    () =>
      navigate(
        SellerOrdersListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: SellerOrdersListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    SellerOrdersListUrlDialog,
    SellerOrdersListUrlQueryParams
  >(navigate, SellerOrdersListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      SellerOrdersListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(SellerOrdersListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { data: previewData } = useSellerOrdersQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const { data: reportData, loading: reportLoading } = useSellerOrdersDownload({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.sellerOrders?.pageInfo,
    paginationState,
    params
  );

  const selectReportData = reportData => {
    const selectedData = reportData?.map(data => {
      delete data.__typename;
      delete data.id;

      data.order_status = data?.order_status?.toUpperCase();
      data.store_order_status = data?.store_order_status?.toUpperCase();
      data.payment_method_type = data?.payment_method_type?.toUpperCase();
      data.order_date = data?.order_date
        ? Moment.tz(new Date(data?.order_date), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;
      data.shipping_schedule = data?.shipping_schedule
        ? Moment.tz(new Date(data?.shipping_schedule), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;
      data.completed_at = data?.completed_at
        ? Moment.tz(new Date(data?.completed_at), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;
      data.store_completed_at = data?.store_completed_at
        ? Moment.tz(new Date(data?.store_completed_at), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;
      return data;
    });
    return selectedData;
  };

  React.useEffect(() => {
    if (
      InvalidThreeMonth(params.orderDateFrom, params.orderDateTo) ||
      InvalidThreeMonth(
        params.orderCompleteDateFrom,
        params.orderCompleteDateTo
      ) ||
      InvalidThreeMonth(params.orderCanceledFrom, params.orderCanceledTo)
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(SellerOrdersListUrl());
    }
  }, [
    params.orderDateFrom,
    params.orderDateTo,
    params.orderCompleteDateFrom,
    params.orderCompleteDateTo
  ]);

  const [downloadData, setDownloadData] = useState([]);

  const shownSellerOrders = previewData?.sellerOrders?.edges.map(
    edge => edge.node
  );
  const allSellerOrders = reportData?.sellerOrders?.edges.map(
    edge => edge.node
  );
  const nextPage = reportData?.sellerOrders?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allSellerOrders]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());
        useDownloadReport(
          selectedReportData,
          reportsDownload.sellerOrders.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Seller Orders successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  return (
    <>
      <SellerOrdersListPage
        sellerOrders={shownSellerOrders}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={reportLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        download={() => openModal("download")}
        onRowClick={id => () => window.open(sellerDetailsUrl(id))}
      />
      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Seller Orders",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a seller order with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{previewData?.sellerOrders?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default SellerOrdersList;
